import * as React from "react"
import { CameraOff, Keyboard, ScanBarcode } from "lucide-react"

import { cn } from "@/lib/utils"
import { BottomSheet } from "@/components/ui/bottom-sheet"
import { Button } from "@/components/ui/button"

/*
  BarcodeScanner — read a barcode / QR code with the phone camera, or type it (18 #10 "Scan & move").

    <ScanButton onDetected={(code) => setSearch(code)} label="Scan part number" />        // icon button + scanner
    <BarcodeScanner open={open} onOpenChange={setOpen} onDetected={(code, { source, format }) => …}
      title="Scan a part number" manualLabel="Part number" manualPlaceholder="e.g. MTN693003" />

  - Uses the browser's BarcodeDetector + the back camera when available: Chrome / Edge on Android and desktop,
    and only on a secure page (https:// or localhost) whose Permissions-Policy allows the camera.
  - Everywhere else (iPhone Safari, Firefox, http://, camera refused, no camera) the sheet says why and the
    manual box is the way in — it is always shown, so nobody is stuck.
  - The camera stops as soon as a code is read, the sheet closes or the component unmounts.
  Props: open, onOpenChange, onDetected(code, { source: 'camera' | 'manual', format }), title, description,
  formats (BarcodeDetector formats; default the common 1D codes + QR), manualLabel, manualPlaceholder,
  testId ('barcode-scanner'). `barcodeSupport()` tells whether camera scanning can work here.
*/
const DEFAULT_FORMATS = ["code_128", "code_39", "code_93", "ean_13", "ean_8", "upc_a", "upc_e", "itf", "qr_code", "data_matrix"]
const SCAN_INTERVAL_MS = 250

function cameraAllowedByPolicy() {
  try {
    const policy = document.permissionsPolicy || document.featurePolicy
    if (policy && typeof policy.allowsFeature === "function") return policy.allowsFeature("camera")
  } catch {
    // older browsers: assume allowed, getUserMedia will tell
  }
  return true
}

/** { camera, detector, ok, reason } — whether camera scanning can work in this browser / page. */
export function barcodeSupport() {
  if (typeof window === "undefined") return { camera: false, detector: false, ok: false, reason: "Not in a browser." }
  const detector = typeof window.BarcodeDetector === "function"
  const secure = window.isSecureContext !== false
  const camera = Boolean(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function")
  let reason = ""
  if (!secure) reason = "Camera scanning needs a secure page (https://)."
  else if (!detector) reason = "This browser cannot read barcodes from the camera (use Chrome on Android)."
  else if (!camera) reason = "No camera is available in this browser."
  else if (!cameraAllowedByPolicy()) reason = "The camera is switched off for this site."
  return { camera, detector, ok: !reason, reason }
}

function cameraErrorText(error) {
  const name = error?.name || ""
  if (name === "NotAllowedError" || name === "SecurityError") return "Camera permission was refused. Allow the camera for this site, or type the code."
  if (name === "NotFoundError" || name === "OverconstrainedError") return "No camera was found on this device."
  if (name === "NotReadableError") return "The camera is in use by another app."
  return "The camera could not start."
}

function BarcodeScanner({
  open,
  onOpenChange,
  onDetected,
  title = "Scan a code",
  description = "Point the camera at the barcode or QR code on the shelf or box.",
  formats = DEFAULT_FORMATS,
  manualLabel = "Or type the code",
  manualPlaceholder = "e.g. MTN693003",
  testId = "barcode-scanner",
}) {
  const videoRef = React.useRef(null)
  const streamRef = React.useRef(null)
  const timerRef = React.useRef(null)
  const doneRef = React.useRef(false)
  const [state, setState] = React.useState({ phase: "idle", message: "" }) // idle | starting | scanning | unavailable | error
  const [manual, setManual] = React.useState("")
  const [manualError, setManualError] = React.useState("")

  const stop = React.useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = null
    const stream = streamRef.current
    streamRef.current = null
    if (stream) stream.getTracks().forEach((track) => track.stop())
    if (videoRef.current) videoRef.current.srcObject = null
  }, [])

  // Callers usually pass inline callbacks (`onDetected={(code) => setQ(code)}`), whose identity changes on every
  // parent render. Keep them in refs so the camera effect depends on `open` only: otherwise each parent render
  // (a clock tick, a keystroke) would stop the stream and call getUserMedia again.
  const onDetectedRef = React.useRef(onDetected)
  const onOpenChangeRef = React.useRef(onOpenChange)
  React.useLayoutEffect(() => {
    onDetectedRef.current = onDetected
    onOpenChangeRef.current = onOpenChange
  })

  const finish = React.useCallback((code, details) => {
    if (doneRef.current) return
    const clean = String(code || "").trim()
    if (!clean) return
    doneRef.current = true
    stop()
    onDetectedRef.current?.(clean, details)
    onOpenChangeRef.current?.(false)
  }, [stop])

  React.useEffect(() => {
    if (!open) {
      stop()
      setState({ phase: "idle", message: "" })
      setManual("")
      setManualError("")
      return undefined
    }
    doneRef.current = false
    const support = barcodeSupport()
    if (!support.ok) {
      setState({ phase: "unavailable", message: support.reason })
      return undefined
    }
    let cancelled = false
    setState({ phase: "starting", message: "Starting the camera…" })
    ;(async () => {
      try {
        let usable = formats
        if (typeof window.BarcodeDetector.getSupportedFormats === "function") {
          const supported = await window.BarcodeDetector.getSupportedFormats()
          usable = formats.filter((format) => supported.includes(format))
        }
        const detector = new window.BarcodeDetector(usable.length ? { formats: usable } : undefined)
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false })
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        streamRef.current = stream
        const video = videoRef.current
        if (video) {
          video.srcObject = stream
          await video.play().catch(() => {})
        }
        setState({ phase: "scanning", message: "Looking for a code…" })
        const tick = async () => {
          if (cancelled || doneRef.current) return
          try {
            if (videoRef.current && videoRef.current.readyState >= 2) {
              const codes = await detector.detect(videoRef.current)
              const found = codes.find((code) => String(code.rawValue || "").trim())
              if (found) {
                finish(found.rawValue, { source: "camera", format: found.format })
                return
              }
            }
          } catch {
            // a frame that cannot be read: try the next one
          }
          timerRef.current = window.setTimeout(tick, SCAN_INTERVAL_MS)
        }
        tick()
      } catch (error) {
        if (!cancelled) setState({ phase: "error", message: cameraErrorText(error) })
      }
    })()
    return () => {
      cancelled = true
      stop()
    }
    // `finish` and `stop` are stable (callbacks are read through refs), so only opening / closing restarts the
    // camera. `formats` is a prop array; restarting the camera when its identity changes is not wanted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, finish, stop])

  const submitManual = (event) => {
    event.preventDefault()
    if (!manual.trim()) {
      setManualError("Type the code first")
      return
    }
    finish(manual, { source: "manual", format: "text" })
  }

  const live = state.phase === "starting" || state.phase === "scanning"

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={title} description={description} testId={testId}>
      <div className="flex flex-col gap-4">
        {live ? (
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-black" data-testid={`${testId}-camera`}>
            <video ref={videoRef} className="h-full w-full object-cover" muted playsInline aria-label="Camera preview" />
            <span className="ui-scan-frame" aria-hidden="true" />
            <p className="absolute inset-x-0 bottom-3 text-center text-[13px] font-semibold text-white drop-shadow" role="status">
              {state.message}
            </p>
          </div>
        ) : null}
        {state.phase === "unavailable" || state.phase === "error" ? (
          <div
            className="flex items-start gap-3 rounded-2xl border border-line bg-surface-2 p-3.5 text-[13.5px] text-ink-2"
            role="status"
            data-testid={`${testId}-unavailable`}
          >
            <span className="ui-icon-tile tone-gray h-9 w-9 rounded-xl" aria-hidden="true">
              {state.phase === "error" ? <CameraOff className="h-[18px] w-[18px]" /> : <ScanBarcode className="h-[18px] w-[18px]" />}
            </span>
            <span><b className="font-semibold text-ink">Camera scanning isn’t available here.</b> {state.message}</span>
          </div>
        ) : null}
        <form onSubmit={submitManual} className="flex flex-col gap-1.5" noValidate>
          <label htmlFor={`${testId}-manual`} className="flex items-center gap-1.5 text-[13px] font-semibold text-ink-2">
            <Keyboard className="h-4 w-4" aria-hidden="true" />{manualLabel}
          </label>
          <div className="flex gap-2">
            <input
              id={`${testId}-manual`}
              value={manual}
              onChange={(event) => {
                setManual(event.target.value)
                if (manualError) setManualError("")
              }}
              placeholder={manualPlaceholder}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              enterKeyHint="go"
              aria-invalid={manualError ? "true" : undefined}
              aria-describedby={manualError ? `${testId}-manual-error` : undefined}
              className={cn(
                "h-12 min-w-0 flex-1 rounded-control border bg-surface px-3 font-mono text-[15px] text-ink outline-none placeholder:font-sans placeholder:text-ink-4 focus-visible:ring-2 focus-visible:ring-ring",
                manualError ? "border-status-red" : "border-line"
              )}
              data-testid={`${testId}-manual`}
            />
            <Button type="submit" size="lg" className="h-12" data-testid={`${testId}-manual-submit`}>Use code</Button>
          </div>
          {manualError ? (
            <p id={`${testId}-manual-error`} className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{manualError}</p>
          ) : null}
        </form>
      </div>
    </BottomSheet>
  )
}

/** Icon button that opens the scanner, e.g. at the end of a search field. */
function ScanButton({ onDetected, label = "Scan a code", className, scannerProps, testId = "scan-button" }) {
  const [open, setOpen] = React.useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        className={cn(
          "grid h-11 w-11 shrink-0 place-items-center rounded-control text-brand-ink hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className
        )}
        data-testid={testId}
      >
        <ScanBarcode className="h-5 w-5" aria-hidden="true" />
      </button>
      <BarcodeScanner open={open} onOpenChange={setOpen} onDetected={onDetected} {...scannerProps} />
    </>
  )
}

export { BarcodeScanner, ScanButton }
